// react-app\src\pages\Billing\PlanSelectionPage.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

// Assuming config exports backendURL correctly
// Make sure backendURL is defined, e.g., "http://localhost:5000/api" or similar
import backendURL from "../../config";

// MUI Components
import { Typography, Button as MuiButton, Box, CircularProgress, Alert, Card, CardContent, CardActions, Grid, Chip } from '@mui/material';

// --- Configuration ---
const API_BASE_URL = backendURL; // Ensure this is correctly set in your config.js

// --- Inline Helper Functions (Keep as is) ---
const formatCurrency = (amount, currencyCode = 'usd') => {
    if (typeof amount !== 'number') return '';
    // Ensure amount is treated as dollars/euros, not cents, for formatting
    const amountInMajorUnits = amount; // Assuming 'price' from backend is already in major units
    try {
         return new Intl.NumberFormat('en-US', {
             style: 'currency',
             currency: currencyCode?.toUpperCase() || 'USD',
             minimumFractionDigits: 2, // Ensure two decimal places
             maximumFractionDigits: 2,
         }).format(amountInMajorUnits);
    } catch(e) {
         console.warn("Currency formatting failed:", e);
         return `$${amountInMajorUnits.toFixed(2)}`; // Fallback
    }
 };

const LoadingSpinner = ({ size = 'medium' }) => {
    const muiSize = size === 'small' ? 20 : size === 'large' ? 40 : 30;
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: size === 'small' ? 0.2 : 1 }}>
            <CircularProgress size={muiSize} />
        </Box>
    );
};

const ErrorMessage = ({ message }) => {
    if (!message) return null;
    return (
        <Alert severity="error" sx={{ my: 2 }}>
            {/* Safely render message, handle potential objects */}
            {typeof message === 'object' ? JSON.stringify(message) : String(message)}
        </Alert>
    );
};

// --- Plan Card Component ---
// No changes needed in PlanCard from the previous "no refresh" version
const PlanCard = ({ plan, onRequestChange, isCurrentPlan, isLoading }) => {
    const { id, display_name, price, currency, interval, trial_days, features } = plan || {};
    if (!plan) return null;
    const isFreemiumIdentifier = display_name === 'Freemium'; // Or maybe 

    let buttonText = 'Select Plan'; let isDisabled = isLoading || isCurrentPlan; let buttonAction = () => {}; let buttonColor = 'primary'; let buttonVariant = 'contained';
    if (isCurrentPlan) { buttonText = 'Current Plan'; buttonColor = 'inherit'; isDisabled = true; buttonVariant = 'outlined'; }
    else { buttonText = `Change Now`; buttonAction = () => { if (onRequestChange && !isDisabled) { onRequestChange(plan); } }; buttonColor = 'primary'; buttonVariant = 'contained'; }
    const featureList = [
        features?.web_pages_limit === -1 ? 'Unlimited Web Pages' : features?.web_pages_limit && `${features.web_pages_limit} Web Pages`,
        features?.document_limit === -1 ? 'Unlimited Documents' : features?.document_limit && `${features.document_limit} Documents`,
        features?.llm_tokens_in_limit === -1 ? 'Unlimited LLM Tokens' : features?.llm_tokens_in_limit && `${(features.llm_tokens_in_limit / 1000000).toFixed(1)}M In Tokens`,
        features?.llm_tokens_out_limit === -1 ? '' : features?.llm_tokens_out_limit && `${(features.llm_tokens_out_limit / 1000000).toFixed(1)}M Out Tokens`,
        features?.multilingual && 'Multilingual Support', features?.google_calendar && 'Google Calendar',
        features?.ecommerce && 'E-commerce Features', features?.ai_agents && 'AI Agents Access'
    ].filter(Boolean);
    return (
        <Card sx={{ border: isCurrentPlan ? '2px solid #4CAF50' : '1px solid #ddd', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: 3, transition: 'box-shadow 0.3s', '&:hover': { boxShadow: 6 }, opacity: isCurrentPlan ? 0.7 : 1, height: '100%' }}>
            <CardContent>
                <Typography variant="h5" component="div" sx={{ mb: 1, color: 'primary.main' }}>{display_name}</Typography>
                <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>{`${formatCurrency(price, currency)} / ${interval}`}</Typography>
                {isFreemiumIdentifier && trial_days === 0 && !isCurrentPlan && (
                    <Typography variant="body2" sx={{ color: 'text.secondary', /* Optional: remove fontStyle: 'italic' */ mb: 2 }}>
                        Unlimited access to core features
                    </Typography>
                )}

                {/* Case 2: Show trial info for non-Freemium plans with trial days > 0 (and not current) */}
                {trial_days > 0 && !isCurrentPlan && !isFreemiumIdentifier && (
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', mb: 2 }}>
                        {trial_days}-day free trial available if eligible
                    </Typography>
                )}                {isCurrentPlan && <Chip label="Current Plan" color="success" size="small" sx={{mb: 1}} />}
                <Box component="ul" sx={{ listStyle: 'none', p: 0, mt: 2, fontSize: '0.9rem', color: 'text.secondary' }}>
                    {featureList.map((feature, index) => (<Typography component="li" key={index} sx={{ mb: 0.5 }}>✓ {feature}</Typography>))}
                    {featureList.length === 0 && <Typography component="li">Basic Features</Typography>}
                </Box>
            </CardContent>
            <CardActions sx={{ justifyContent: 'center', p: 2, mt: 'auto' }}>
                <MuiButton variant={buttonVariant} color={buttonColor} onClick={buttonAction} disabled={isDisabled} fullWidth startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}>{isLoading ? 'Processing...' : buttonText}</MuiButton>
            </CardActions>
        </Card>
    );
};


// ========================================================================
// --- Main Plan Selection Page Component ---
// ========================================================================
const PlanSelectionPage = ({ onComplete, onError }) => {
    const [plans, setPlans] = useState([]);
    const [currentSubscription, setCurrentSubscription] = useState(null);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [initialError, setInitialError] = useState(null);
    const [actionError, setActionError] = useState(null);
    const [isActionLoading, setIsActionLoading] = useState(false);
    const [selectedPlanForAction, setSelectedPlanForAction] = useState(null);

    const onErrorRef = useRef(onError);
    useEffect(() => { onErrorRef.current = onError; }, [onError]);

    // --- Authentication & Headers ---
    const getAuthHeaders = useCallback(() => {
        const token = localStorage.getItem('access_token');
        if (!token) {
            console.error("Auth token not found.");
            // Avoid calling onError directly here, let the calling function handle it
            // if (onErrorRef.current) onErrorRef.current("Authentication required.");
            return null;
        }
        return { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };
     }, []); // Empty dependency array is correct here

    // --- Local Storage Update ---
    const updateLocalStorageSubscription = useCallback((newSubscriptionData) => {
         // Ensure newSubscriptionData is valid before proceeding
         if (!newSubscriptionData || typeof newSubscriptionData !== 'object') {
             console.warn("Attempted to update local storage with invalid subscription data:", newSubscriptionData);
             return;
         }
         try {
            const userStr = localStorage.getItem('user');
            if (userStr) {
                const userData = JSON.parse(userStr);
                // Directly replace the subscription part
                userData.subscription = newSubscriptionData;
                if (userData.subscription && userData.subscription?.plan?.price > 0){
                    userData.has_used_trial = true
                }
                localStorage.setItem('user', JSON.stringify(userData));
                window.dispatchEvent(new CustomEvent('userLocalStorageUpdated', { detail: userData  }));
                console.log("Local storage user subscription updated.");
            }
         } catch (e) { console.error("Error updating user subscription in local storage:", e); }
    }, []);

    // --- Data Fetching ---
    const fetchInitialData = useCallback(async () => {
        console.log("Fetching initial data..."); // Log start
        setIsInitialLoading(true);
        setInitialError(null);
        setActionError(null);
        setSelectedPlanForAction(null);

        const headers = getAuthHeaders();
        if (!headers) {
            setIsInitialLoading(false);
            const authError = "Authentication token is missing. Please log in.";
            setInitialError(authError);
            if (onErrorRef.current) onErrorRef.current(authError); // Notify parent on auth failure
            return;
        }

        try {
            // Fetch plans and current subscription concurrently
            const plansPromise = axios.get(`${API_BASE_URL}/plans`); // Assuming plans don't need auth
            const subPromise = axios.get(`${API_BASE_URL}/subscriptions/current`, { headers });

            const [plansResult, subResult] = await Promise.allSettled([plansPromise, subPromise]);

            let fetchedSubscription = null;
            let subError = null;

            // Process Plans
            if (plansResult.status === 'fulfilled' && plansResult.value?.data) {
                // Assuming the API returns an array of plans directly or under a 'data' key
                const plansData = Array.isArray(plansResult.value.data) ? plansResult.value.data : plansResult.value.data.data; // Adjust based on actual structure
                if (Array.isArray(plansData)) {
                    setPlans(plansData);
                    console.log("Plans fetched:", plansData);
                } else {
                    console.error("Plans data is not an array:", plansResult.value.data);
                    setInitialError("Received invalid plan data structure from server.");
                }
            } else {
                console.error("Plan fetch failed:", plansResult.reason || "Unknown reason");
                setInitialError(plansResult.reason?.response?.data?.message || "Could not load available plans.");
            }

            // Process Current Subscription
            if (subResult.status === 'fulfilled' && subResult.value?.data) {
                 // Check common structures: directly in .data or nested under .data.data
                 fetchedSubscription = subResult.value.data.data || subResult.value.data;
                 console.log("Subscription fetched:", fetchedSubscription);
                 // Basic validation
                 if (!fetchedSubscription || !fetchedSubscription.plan) {
                     console.warn("Fetched subscription data seems incomplete:", fetchedSubscription);
                     // Decide if this is an error or implicit freemium based on status
                     if (fetchedSubscription?.status !== 'freemium') {
                         fetchedSubscription = null; // Treat incomplete as error unless explicitly freemium
                         subError = "Received incomplete subscription data.";
                     }
                 }
            } else if (subResult.reason?.response?.status === 404) {
                 // Handle 404: Check if backend provides implicit freemium data
                 const responseData = subResult.reason.response.data;
                 fetchedSubscription = responseData?.data?.plan?.name === 'freemium' ? responseData.data : null;
                 console.log("Subscription fetch resulted in 404, resolved to:", fetchedSubscription ? "Implicit Freemium" : "No Subscription");
            } else if (subResult.reason?.response?.status === 401) {
                 console.error("Subscription fetch failed (401):", subResult.reason);
                 subError = "Your session may have expired. Please log in again.";
                 if (onErrorRef.current) onErrorRef.current("Session expired."); // Notify parent
            } else {
                 console.error("Subscription fetch failed:", subResult.reason);
                 subError = subResult.reason?.response?.data?.message || "Failed to load current subscription details.";
            }

            setCurrentSubscription(fetchedSubscription);

            if(subError) {
                // Combine initial error with subscription error if both occur
                setInitialError(prev => [prev, subError].filter(Boolean).join('; '));
            }

        } catch (err) {
            console.error("Unexpected error during initial data fetch:", err);
            const unexpectedError = err.message || "An unexpected error occurred.";
            setInitialError(unexpectedError);
            if (onErrorRef.current) onErrorRef.current(unexpectedError);
        } finally {
            setIsInitialLoading(false);
            console.log("Initial data fetch finished.");
        }
    }, [getAuthHeaders]); // dependency fetchInitialData needs getAuthHeaders

    // Fetch data on component mount
    useEffect(() => {
        fetchInitialData();
    }, [fetchInitialData]); // Include fetchInitialData in dependency array

    // ===============================================
    // --- Action for IMMEDIATE Plan Change ---
    // ===============================================
    const handleImmediateChange = useCallback(async (planToChangeTo) => {
        if (!planToChangeTo || isActionLoading || !currentSubscription) { // Added check for currentSubscription
            console.warn("Change aborted: No plan selected, already loading, or no current subscription.");
            return;
        }

        // Prevent changing to the same plan
        if(currentSubscription?.plan?.id === planToChangeTo.id) {
            console.warn("Attempted to change to the current plan.");
            return;
        }

        setIsActionLoading(true);
        setActionError(null);
        setSelectedPlanForAction(planToChangeTo); // Track which button is loading

        const headers = getAuthHeaders();
        if (!headers) {
            setIsActionLoading(false);
            setSelectedPlanForAction(null);
            setActionError("Authentication failed. Cannot change plan.");
            if (onErrorRef.current) onErrorRef.current("Authentication failed.");
            return;
        }

        console.log(`Requesting IMMEDIATE change to plan ID: ${planToChangeTo.id} from plan ID: ${currentSubscription.plan?.id}`);
        try {
            const response = await axios.post(`${API_BASE_URL}/subscriptions/current/change-immediate`,
                { new_plan_id: planToChangeTo.id }, // Ensure body has the correct key
                { headers }
            );

            // --- DEBUGGING: Log the entire response ---
            console.log('API Response for change-immediate:', response);

            let updatedSubscriptionData = null;
            let successMessage = null;
            let isSuccess = false;
            let requiresAction = false;

            // Check for successful status codes (200 OK, 202 Accepted)
            if (response.status === 200 && response.data) {
                // Check common success patterns
                 if(response.data.status === 'success' && response.data.data) {
                     updatedSubscriptionData = response.data.data;
                     successMessage = response.data.message;
                     isSuccess = true;
                 } else if (response.data.subscription_id && response.data.plan) { // Check if response.data IS the sub object
                     updatedSubscriptionData = response.data;
                     successMessage = response.data.message || `Successfully changed plan to ${planToChangeTo.display_name}!`;
                     isSuccess = true;
                 } else {
                     // Response is 200, but structure unknown - log warning
                     console.warn("Received 200 OK but unexpected success data structure:", response.data);
                     // Assume success but cannot update UI automatically
                     successMessage = response.data.message || `Plan change to ${planToChangeTo.display_name} initiated, but UI might not update immediately.`;
                     isSuccess = true; // Treat as success for messaging
                     updatedSubscriptionData = null; // Cannot update state
                 }

            } else if (response.status === 202 && response.data?.status === 'requires_action') {
                 requiresAction = true;
                 successMessage = response.data.message || "Payment confirmation required."; // Use message if available
                 // Extract necessary data for SCA handling if implemented later
                 // const clientSecret = response.data.data?.client_secret;
            } else {
                 // Treat other non-2xx responses as errors
                 const errorMsg = response.data?.message || response.data?.error || `Request failed with status ${response.status}`;
                 throw new Error(errorMsg);
            }

            // --- Process Result ---
            if (isSuccess) {
                console.log("Immediate plan change processed successfully by backend.");
                setActionError(null);

                if (updatedSubscriptionData && updatedSubscriptionData.plan) {
                    console.log("Setting current subscription state with:", updatedSubscriptionData);
                    setCurrentSubscription(updatedSubscriptionData); // Update local React state
                    updateLocalStorageSubscription(updatedSubscriptionData); // Update browser storage
                } else {
                    console.warn("Success response received, but no valid subscription data to update UI state. Manual refresh might be needed.");
                    // Optionally trigger a delayed refresh or show a specific message
                }

                if (onComplete) onComplete(successMessage || `Successfully changed plan to ${planToChangeTo.display_name}!`);

            } else if (requiresAction) {
                console.warn("Immediate plan change requires payment confirmation (SCA).");
                setActionError(successMessage); // Show the requires_action message as an info/error
                // TODO: Implement SCA flow here if necessary
            }

        } catch (err) {
             // --- NETWORK OR BACKEND ERROR ---
            console.error("Immediate plan change request failed:", err);
             const errorMsg = err.response?.data?.message || err.response?.data?.error || err.message || "Failed to change plan immediately.";
            setActionError(errorMsg);
            if (onErrorRef.current) onErrorRef.current(errorMsg);

        } finally {
            setIsActionLoading(false);
            setSelectedPlanForAction(null); // Clear loading indicator for the specific button
        }

    }, [isActionLoading, currentSubscription, getAuthHeaders, onComplete, updateLocalStorageSubscription, setCurrentSubscription]); // Dependencies

    // --- Render Logic ---
    if (isInitialLoading) {
        return <Box sx={{ p: 5, textAlign: 'center' }}><LoadingSpinner size="large" /></Box>;
    }

    // Show critical error if auth failed or no plans loaded at all
    if (!getAuthHeaders() || (initialError && !plans.length)) {
         return <ErrorMessage message={`Failed to load page: ${initialError || 'Authentication is required.'}`} />;
    }

    // Get current plan ID safely AFTER initial loading is complete
    const currentPlanId = currentSubscription?.plan?.id;

    return (
        <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, maxWidth: '1200px', mx: 'auto' }}>
            <Typography variant="h4" component="h2" sx={{ textAlign: 'center', mb: 2 }}>
                 {currentSubscription ? 'Change Your Plan' : 'Choose Your Plan'}
            </Typography>
            <Typography variant="body1" sx={{ textAlign: 'center', mb: 3, color: 'text.secondary' }}>
                {currentSubscription
                    ? `Select a plan below to change immediately. Any plan other than the Free plan will use your default active card payment method. ${currentSubscription.status === 'trialing' ? 'Your trial period may be adjusted based on the new plan.' : 'Prorated charges or credits may apply.'}`
                    : 'Please select a plan to subscribe.'
                }
            </Typography>

             {/* Display non-critical initial errors (e.g., failed sub load but plans loaded) */}
             {initialError && plans.length > 0 && <ErrorMessage message={initialError} />}
             {/* Display action errors */}
             {actionError && <ErrorMessage message={actionError} />}

            <Grid container spacing={3} justifyContent="center" alignItems="stretch">
                {plans.length > 0 ? (
                    plans.map(plan => (
                        <Grid item key={plan.id} xs={12} sm={6} md={4} lg={3} sx={{ display: 'flex' }}>
                            <PlanCard
                                plan={plan}
                                onRequestChange={handleImmediateChange}
                                // Safely compare plan IDs only after initial loading
                                isCurrentPlan={!isInitialLoading && plan.id === currentPlanId}
                                // Show loading only for the specific plan being acted upon
                                isLoading={isActionLoading && selectedPlanForAction?.id === plan.id}
                            />
                        </Grid>
                    ))
                ) : ( !isInitialLoading && // Only show "No plans" if not loading and plans array is empty
                     <Grid item xs={12}> <Typography sx={{ textAlign: 'center', my: 3 }}>No plans available.</Typography> </Grid>
                )}
            </Grid>

        </Box>
    );
};

export default PlanSelectionPage;